import { savePhaseAreaResize } from "./save-phase-area-resize.js";
import {
  planSolvePhaseAreaGrowth,
  type SolvePhaseAreaGrowth,
} from "@playbookai/widget-schemas";
import { readPhaseAreas } from "./phase-area-doc-shape.js";
import { nanoid } from "nanoid";
import * as Y from "yjs";
import type { WidgetKind, SolvePhase } from "@playbookai/types";
import { getWidget, toast } from "@playbookai/ui";
import { widgetsMap } from "@playbookai/collab-client";
import {
  widgetsApi,
  widgetPlacementsApi,
  type ApiClientConfig,
  type PlacementResponse,
} from "../api/playbook-be-client.js";
import { reserveManualWidgetPosition } from "./manual-widget-placement.js";

/**
 * Add a widget to a SOLVE canvas. Every widget kind is SQL-backed (sticky
 * notes are NOT a widget kind — they live in a separate canvas-only Y.Map
 * key, see `notes.ts`). Flow:
 *
 *  1. `POST /v1/widgets` — create the entity (widget.widgets row).
 *  2. `POST /v1/widget-placements` — pin to `solve_canvas:<solveId>`.
 *  3. Write a `{kind, widgetId, placementId, x, y, w, h}` reference into the
 *     canvas Y.Map.
 *
 * Returns the Y.Map key used to store the placement.
 */
export async function addWidget(
  doc: Y.Doc,
  kind: WidgetKind,
  phase: SolvePhase | "any",
  solveId: string,
  apiConfig: ApiClientConfig,
  onWidgetPlaced?: (placement: PlacementResponse) => void,
  areaGrowth?: {
    visiblePhases: readonly SolvePhase[];
    canEditPhase: (phase: SolvePhase) => boolean;
    onPlacementsSaved: (placements: readonly PlacementResponse[]) => void;
  },
): Promise<string> {
  const def = getWidget(kind);
  if (!def) throw new Error(`addWidget: unknown kind '${kind}'`);
  if (def.storage !== "sql") {
    throw new Error(`addWidget: kind '${kind}' is not SQL-backed`);
  }

  const checkGrowth = (
    position: Parameters<typeof planSolvePhaseAreaGrowth>[1],
  ): SolvePhaseAreaGrowth | null => {
    const phases =
      areaGrowth?.visiblePhases ?? (phase === "any" ? [] : [phase]);
    const plan = planSolvePhaseAreaGrowth(
      phase,
      position,
      readPhaseAreas(doc),
      phases,
    );
    if (
      plan &&
      (!areaGrowth ||
        plan.rects.some(([affected]) => !areaGrowth.canEditPhase(affected)))
    ) {
      throw new Error(
        "This widget needs more space, but growing its area would move a locked phase. Unlock the affected phase or ask an owner to add it.",
      );
    }
    return plan;
  };
  const preview = reserveManualWidgetPosition(doc, phase, kind);
  try {
    checkGrowth(preview.position);
  } finally {
    preview.release();
  }
  const ymapKey = nanoid();

  const widget = await widgetsApi.create(apiConfig, {
    kind,
    name: def.label,
    params: def.defaultParams as Record<string, unknown>,
  });
  // Read live geometry after entity creation, immediately before placement.
  // The size comes back WITH the position: the shared placement rule resolves it
  // from the widget kind, which is what keeps a hand-added widget and an
  // AI-created one of the same kind the same size.
  let reservation: ReturnType<typeof reserveManualWidgetPosition> | undefined;
  let placement: PlacementResponse | undefined;
  let committed = false;
  try {
    reservation = reserveManualWidgetPosition(doc, phase, kind);
    const { x, y, w, h } = reservation.position;
    checkGrowth(reservation.position);
    placement = await widgetPlacementsApi.upsert(apiConfig, {
      widgetId: widget.id,
      surface: "solve_canvas",
      surfaceId: solveId,
      position: { x, y, w, h },
      // The upsert is the single authoritative phase source (ADR-0019) — the
      // Yjs doc only mirrors it. "any" (phase-agnostic) persists as NULL.
      ...(phase !== "any" ? { phase } : {}),
    });

    const savedPlacement = placement;
    const growth = checkGrowth(reservation.position);
    const carried = await savePhaseAreaResize(
      doc,
      growth,
      solveId,
      apiConfig,
      () => {
        const wmap = new Y.Map<unknown>();
        wmap.set("kind", kind);
        wmap.set("phase", phase);
        wmap.set("widgetId", widget.id);
        wmap.set("placementId", savedPlacement.id);
        wmap.set("x", x);
        wmap.set("y", y);
        wmap.set("w", w);
        wmap.set("h", h);
        wmap.set("z", Date.now());
        wmap.set("createdAt", Date.now());
        widgetsMap(doc).set(ymapKey, wmap);
      },
    );

    committed = true;
    // Notifications must not turn a committed add into a retryable failure.
    // All carry persistence completed before these independent cache notifications.
    for (const notify of [
      () => onWidgetPlaced?.(savedPlacement),
      () => {
        if (carried.length) areaGrowth?.onPlacementsSaved(carried);
      },
    ]) {
      try {
        notify();
      } catch (error) {
        console.warn("[solve] Widget added; follow-up failed", error);
        toast(
          "The widget was added. Refresh the canvas to check its latest layout.",
          {
            title: "Widget added; refresh needed",
            tone: "watch",
          },
        );
      }
    }
    return ymapKey;
  } catch (error) {
    // Compensate completed REST writes before surfacing a failed add. Never
    // delete a committed canvas widget because a notification callback threw.
    if (!committed) {
      const cleanupErrors: unknown[] = [];
      if (placement) {
        try {
          await widgetPlacementsApi.delete(apiConfig, placement.id);
        } catch (cleanupError) {
          cleanupErrors.push(cleanupError);
        }
      }
      try {
        await widgetsApi.delete(apiConfig, widget.id);
      } catch (cleanupError) {
        cleanupErrors.push(cleanupError);
      }
      if (cleanupErrors.length) {
        throw new AggregateError(
          [error, ...cleanupErrors],
          "Widget add failed and cleanup could not finish. Refresh the canvas to check for a saved widget before retrying.",
          { cause: error },
        );
      }
    }
    throw error;
  } finally {
    reservation?.release();
  }
}
